/**
 * Step 2 — choose a provider and run a check.
 *
 * `GET /verification/moderator/providers/?company=&registration=` lists every
 * provider with its tags and where it ranks in the routing recommendation for
 * the registration's jurisdiction (`data/routing.json`). Recommendations only:
 * any configured provider may be run, and nothing runs without a click.
 * The manual provider records official-registry evidence typed by the moderator.
 */
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "@/lib/apiError";
import { isRouteRole, isUseCase, jurisdictionLabel } from "@/lib/kyb";
import type {
  _QueueRegistration,
  VerificationProvider,
  VerificationProviderList,
} from "@/services/moderators/generated/model";
import {
  useVerificationModeratorCompaniesStartCreate,
  useVerificationModeratorProvidersList,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";

// The manual provider's evidence form (apps/verification/providers/manual.py).
const MANUAL_PROVIDER = "manual";
const MANUAL_FIELDS = ["registryName", "registryUrl", "matchedName", "registrationNumber", "entityStatus"] as const;
type ManualField = (typeof MANUAL_FIELDS)[number] | "note";
const MANUAL_REQUIRED: ManualField[] = ["registryName", "registryUrl", "matchedName", "registrationNumber"];

export const KybProviderPicker = ({
  companyId,
  registrations,
  onStarted,
}: {
  companyId: number;
  registrations: _QueueRegistration[];
  onStarted: () => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [registrationId, setRegistrationId] = useState<number | undefined>(registrations[0]?.id);
  const [manualOpen, setManualOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, error } = useVerificationModeratorProvidersList({
    company: companyId,
    ...(registrationId ? { registration: registrationId } : {}),
  });
  const list = data as VerificationProviderList | undefined;

  const start = useVerificationModeratorCompaniesStartCreate({
    mutation: {
      onSuccess: () => {
        setManualOpen(false);
        onStarted();
      },
    },
  });

  const providers = [...(list?.providers ?? [])].sort(
    (a, b) => (a.recommendedRank ?? 99) - (b.recommendedRank ?? 99) || Number(b.configured) - Number(a.configured),
  );
  const visible = showAll ? providers : providers.filter((p) => p.integrated);
  const hiddenCount = providers.length - visible.length;

  const run = (provider: VerificationProvider, options?: Record<string, string>) =>
    start.mutate({
      id: companyId,
      data: { provider: provider.key, registrationId: registrationId ?? null, ...(options ? { options } : {}) },
    });

  return (
    <section className="kyb-section">
      <h2 className="kyb-section__title">
        <span className="mod-page__chip">2</span> {t("providers.title")}
      </h2>
      <p className="kyb-section__help">{t("providers.help")}</p>

      {registrations.length === 0 ? (
        <div className="kyb-callout kyb-callout--warning">{t("providers.noRegistration")}</div>
      ) : (
        <label className="kyb-form">
          {t("providers.registration")}
          <select
            className="mod-page__select"
            value={registrationId ?? ""}
            onChange={(e) => setRegistrationId(Number(e.target.value))}
          >
            {registrations.map((r) => (
              <option key={r.id} value={r.id}>
                {jurisdictionLabel(r.country, r.subdivision)} · {r.registrationNumber} ({r.status})
              </option>
            ))}
          </select>
        </label>
      )}

      {list?.route ? (
        <div className="kyb-callout">
          <strong>
            {t("providers.route.title", { name: list.route.name, jurisdiction: list.jurisdiction ?? "" })}
          </strong>
          <div>{t("providers.route.primary", { list: list.route.primary.join(", ") || "—" })}</div>
          {list.route.backup.length > 0 ? (
            <div>{t("providers.route.backup", { list: list.route.backup.join(", ") })}</div>
          ) : null}
          {list.route.free.length > 0 ? (
            <div>{t("providers.route.free", { list: list.route.free.join(", ") })}</div>
          ) : null}
          {list.route.officialSource ? (
            <div>{t("providers.route.official", { source: list.route.officialSource })}</div>
          ) : null}
          {list.route.owners ? <div>{t("providers.route.owners", { owners: list.route.owners })}</div> : null}
        </div>
      ) : null}

      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("providers.loadError")}</div> : null}

      {visible.map((provider) => (
        <ProviderCard
          key={provider.key}
          provider={provider}
          busy={start.isPending}
          canRun={registrations.length > 0}
          onRun={() => (provider.key === MANUAL_PROVIDER ? setManualOpen((v) => !v) : run(provider))}
        >
          {provider.key === MANUAL_PROVIDER && manualOpen ? (
            <ManualEvidenceForm
              busy={start.isPending}
              defaultNumber={registrations.find((r) => r.id === registrationId)?.registrationNumber ?? ""}
              onSubmit={(options) => run(provider, options)}
              onCancel={() => setManualOpen(false)}
            />
          ) : null}
        </ProviderCard>
      ))}

      {start.isError ? (
        <div className="mod-page__inline-error">{getApiErrorMessage(start.error, t("providers.startError"))}</div>
      ) : null}

      {hiddenCount > 0 || showAll ? (
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setShowAll((v) => !v)}>
          {showAll ? t("providers.hideBenchmarked") : t("providers.showBenchmarked", { count: hiddenCount })}
        </button>
      ) : null}
    </section>
  );
};

const ProviderCard = ({
  provider,
  busy,
  canRun,
  onRun,
  children,
}: {
  provider: VerificationProvider;
  busy: boolean;
  canRun: boolean;
  onRun: () => void;
  children?: ReactNode;
}) => {
  const { t } = useTranslation("kyb");
  const recommended = provider.recommendedRank !== null && provider.recommendedRank !== undefined;
  const runnable = provider.integrated && provider.configured && canRun && !provider.openCheckId;
  // Say why a provider can't run instead of only greying the button out.
  const blocked = !provider.configured ? "noCredentials" : !canRun ? "noRegistration" : null;
  const classes = [
    "kyb-provider",
    recommended ? "kyb-provider--recommended" : "",
    provider.integrated && provider.configured ? "" : "kyb-provider--unavailable",
  ].join(" ");

  return (
    <article className={classes}>
      <div className="kyb-provider__head">
        <div>
          <p className="kyb-provider__name">
            {provider.label}{" "}
            {recommended ? (
              <span className="mod-page__chip mod-page__chip--success">
                {isRouteRole(provider.routeRole)
                  ? t(`providers.role.${provider.routeRole}`)
                  : t("providers.recommended", { rank: provider.recommendedRank })}
              </span>
            ) : null}
            {!provider.integrated ? <span className="mod-page__chip">{t("providers.notIntegrated")}</span> : null}
            {provider.integrated && !provider.configured ? (
              <span className="mod-page__chip mod-page__chip--warning">{t("providers.notConfigured")}</span>
            ) : null}
            {provider.integrated ? (
              <span className={`mod-page__chip ${provider.badgeEligible ? "" : "mod-page__chip--warning"}`}>
                {provider.badgeEligible ? t("providers.badgeEligible") : t("providers.notBadgeEligible")}
              </span>
            ) : null}
          </p>
          <p className="kyb-note">{provider.description || provider.bestFor}</p>
        </div>
        {provider.integrated ? (
          provider.openCheckId ? (
            <a className="mod-page__btn mod-page__btn--ghost" href={`#check-${provider.openCheckId}`}>
              {t("providers.openCheck", { id: provider.openCheckId })}
            </a>
          ) : (
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={!runnable || busy}
              onClick={onRun}
            >
              {provider.key === MANUAL_PROVIDER ? t("providers.enterEvidence") : t("providers.run")}
            </button>
          )
        ) : null}
      </div>
      {provider.integrated && blocked && !provider.openCheckId ? (
        <div className="kyb-callout kyb-callout--warning">{t(`providers.blocked.${blocked}`)}</div>
      ) : null}

      <div className="kyb-provider__tags">
        {provider.useCases.map((u) => (
          <span key={u} className="mod-page__chip">
            {isUseCase(u) ? t(`useCase.${u}`) : u}
          </span>
        ))}
        {provider.jurisdictions.map((j) => (
          <span key={j} className="mod-page__chip">
            <i className="fa-solid fa-location-dot" /> {j}
          </span>
        ))}
      </div>

      <div className="kyb-bench">
        {provider.bestFor && provider.description ? (
          <span>{t("providers.bestFor", { text: provider.bestFor })}</span>
        ) : null}
        {provider.costNote ? <span>{t("providers.cost", { text: provider.costNote })}</span> : null}
        {provider.candidateSelection ? <span>{t("providers.candidateSelection")}</span> : null}
        {provider.webhook && provider.webhook !== "none" ? (
          <span>{t("providers.webhook", { kind: provider.webhook })}</span>
        ) : null}
      </div>
      {Object.keys(provider.benchmark ?? {}).length > 0 ? (
        <div className="kyb-bench" title={t("providers.benchmarkHelp")}>
          {Object.entries(provider.benchmark).map(([region, score]) => (
            <span key={region}>
              {region} <span className="kyb-bench__dots">{"●".repeat(score) + "○".repeat(Math.max(0, 5 - score))}</span>
            </span>
          ))}
        </div>
      ) : null}
      {children}
    </article>
  );
};

const ManualEvidenceForm = ({
  busy,
  defaultNumber,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  defaultNumber: string;
  onSubmit: (options: Record<string, string>) => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [values, setValues] = useState<Record<ManualField, string>>({
    registryName: "",
    registryUrl: "",
    matchedName: "",
    registrationNumber: defaultNumber,
    entityStatus: "active",
    note: "",
  });
  const [missing, setMissing] = useState(false);

  const set = (field: ManualField, value: string) => setValues((v) => ({ ...v, [field]: value }));
  const submit = () => {
    if (MANUAL_REQUIRED.some((f) => !values[f].trim())) {
      setMissing(true);
      return;
    }
    setMissing(false);
    onSubmit(Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()])));
  };

  return (
    <div className="kyb-form kyb-sub">
      <p className="kyb-note">{t("providers.manual.help")}</p>
      <div className="kyb-form__grid">
        {MANUAL_FIELDS.map((field) => (
          <label key={field}>
            {t(`providers.manual.fields.${field}`)}
            <input
              className="mod-page__input"
              type={field === "registryUrl" ? "url" : "text"}
              value={values[field]}
              onChange={(e) => set(field, e.target.value)}
            />
          </label>
        ))}
      </div>
      <label>
        {t("providers.manual.fields.note")}
        <textarea
          className="mod-page__input"
          rows={2}
          value={values.note}
          onChange={(e) => set("note", e.target.value)}
        />
      </label>
      {missing ? <div className="mod-page__inline-error">{t("providers.manual.missing")}</div> : null}
      <div className="mod-page__form-row">
        <button type="button" className="mod-page__btn mod-page__btn--primary" disabled={busy} onClick={submit}>
          {busy ? t("providers.starting") : t("providers.manual.submit")}
        </button>
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onCancel}>
          {t("common:actions.cancel")}
        </button>
      </div>
    </div>
  );
};
